import React from 'react'
import { Link } from 'react-router-dom'
import { Compass, Activity, Users, ShieldCheck } from 'lucide-react'

export default function Platform() {
  return (
    <section className="section light" style={{ minHeight: '70vh' }}>
      <h2>The Platform</h2>
      <p className="lead muted">
        Two capabilities, working together underneath the systems your organization already runs on.
      </p>

      <div className="flow-columns">
        <div className="flow-column">
          <h3><Compass size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />Route for Better Fit</h3>
          <p className="muted">
            Help route patients into existing provider networks using richer intake information rather
            than availability alone.
          </p>
          <div className="flow-diagram compact">
            <div className="flow-step">Structured intake</div>
            <div className="flow-arrow">↓</div>
            <div className="flow-step">Fit-informed routing</div>
            <div className="flow-arrow">↓</div>
            <div className="flow-step">Provider selection</div>
          </div>
        </div>
        <div className="flow-column">
          <h3><Activity size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />Improve Early Continuity</h3>
          <p className="muted">
            Identify when patients may be drifting away from care so providers can intervene earlier.
          </p>
          <div className="flow-diagram compact">
            <div className="flow-step">Appointment begins</div>
            <div className="flow-arrow">↓</div>
            <div className="flow-step">Continuity monitoring</div>
            <div className="flow-arrow">↓</div>
            <div className="flow-step">Risk detection</div>
            <div className="flow-arrow">↓</div>
            <div className="flow-step">Provider alert</div>
            <div className="flow-arrow">↓</div>
            <div className="flow-step">Session 4 continuity</div>
          </div>
        </div>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>How Continuity Monitoring Works</h2></div>
        <p className="lead muted">
          Continuity monitoring runs quietly in the background of care that's already underway — it doesn't
          require a new workflow or a new place to log in.
        </p>
        <div className="cards">
          <div className="card"><h3>Monitor scheduling activity</h3><p>Track whether follow-up appointments are being scheduled as expected.</p></div>
          <div className="card"><h3>Monitor rebooking patterns</h3><p>Notice when a rebooking is delayed, skipped, or trending later than usual.</p></div>
          <div className="card"><h3>Identify potential disengagement signals</h3><p>Surface patterns that may indicate a patient is drifting away from care.</p></div>
          <div className="card"><h3>Notify providers</h3><p>Route a continuity alert to the provider or care team responsible for that patient.</p></div>
          <div className="card"><h3>Support timely outreach</h3><p>Give care teams the lead time to reach out before a gap in care becomes a dropout.</p></div>
        </div>
        <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
          We don't publish the internal scoring methodology behind continuity monitoring — the operational
          workflow above is the accurate, complete description of what it does.
        </p>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Why Session 4 Matters</h2></div>
        <p className="lead muted">
          ATUUNE uses Session 4 — a patient's fourth session with their routed provider — as an operational
          milestone representing early continuity, not as proof of clinical success.
        </p>
        <div className="cards">
          <div className="card">
            <Users />
            <h3>Clarity for organizations</h3>
            <p>See who's engaging, who needs a check-in, and where support is needed — all in one place.</p>
          </div>
          <div className="card">
            <ShieldCheck />
            <h3>Reasons, not a black box</h3>
            <p>Every routing suggestion comes with a plain-language reason — never an unexplained score.</p>
          </div>
        </div>
      </div>

      <div className="actions" style={{ marginTop: 28 }}>
        <Link to="/how-it-works">See How It Works</Link>
        <Link className="secondary outline-dark" to="/contact">Request Demo</Link>
      </div>
    </section>
  )
}
